import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Typography, Progress, Spin, Switch, Tabs, Empty, Tag, Space } from 'antd';
import axios from 'axios';
import AdminMenu from '../components/AdminMenu';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend);

const { Title, Text } = Typography;

/* ---------------- helpers ---------------- */
function filtrarOrdenarDiscos(disks = []) {
  const out = { root: null, discos: [] };
  for (const d of disks) {
    const m = d.mount || d.fs || '';
    if (m === '/') out.root = d;
    else if (/^\/mnt\/Disco\d+$/i.test(m)) out.discos.push(d);
  }
  out.discos.sort((a, b) => {
    const an = parseInt((a.mount || a.fs).match(/Disco(\d+)/i)?.[1] ?? '999999', 10);
    const bn = parseInt((b.mount || b.fs).match(/Disco(\d+)/i)?.[1] ?? '999999', 10);
    return an - bn;
  });
  return out;
}

const pct = (n) => {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, Number(v.toFixed(1))));
};

/** “Siembra” la serie para que el gráfico se vea aunque haya 1 punto */
const pushSample = (arr, val) => {
  const a = [...(arr ?? []).slice(-19), Number(val ?? 0)];
  if (a.length < 2) return Array(20).fill(a[0]);
  return a;
};

// Línea con rango 0–100 y altura controlada
const lineChart = (label, data, color) => ({
  data: {
    labels: Array(data.length).fill(''),
    datasets: [
      { label, data, borderColor: color, backgroundColor: color, tension: 0.3, pointRadius: 2 }
    ]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    scales: { y: { min: 0, max: 100, ticks: { stepSize: 20 } } },
    plugins: { legend: { display: false }, tooltip: { enabled: true } }
  }
});

/* ---------------- component ---------------- */
function AdminMonitor() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verGraficos, setVerGraficos] = useState(true);
  const [history, setHistory] = useState({ cpu: [], memory: [], gpu: [] });
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await axios.get('https://mundiemby.us.to/api/monitor'); // usa tu ruta
        setStats(data);
        setLoading(false);

        setHistory(prev => {
          const gpus = data.gpu?.controllers ?? [];
          const next = {
            cpu: pushSample(prev.cpu, data.cpu?.usage ?? 0),
            memory: pushSample(prev.memory, data.memory?.usagePercent ?? 0),
            gpu: []
          };
          for (let i = 0; i < gpus.length; i++) {
            next.gpu[i] = pushSample(prev.gpu?.[i] ?? [], gpus[i]?.utilizationGpu ?? 0);
          }
          return next;
        });
      } catch (err) {
        console.error('Error al obtener estadísticas del servidor:', err);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !stats) {
    return (
      <>
        <AdminMenu />
        <div style={{ padding: 80, textAlign: 'center' }}>
          <Spin tip="Cargando datos del servidor..." size="large">
            <div style={{ height: 120 }} />
          </Spin>
        </div>
      </>
    );
  }

  const { cpu, memory, network, disks, gpu } = stats;
  const { root, discos } = filtrarOrdenarDiscos(disks);
  const totalDiscosMontados = discos.length;

  const tabStyle = (activeKey, key) => ({
    backgroundColor: activeKey === key ? '#fff' : '#1f1f1f',
    color: activeKey === key ? '#000' : '#fff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontWeight: 'bold',
    marginRight: '8px',
    cursor: 'pointer',
  });

  const renderDiskCard = (disk) => {
    const mountLabel = disk.mount || disk.fs || '—';
    const usedGB = ((disk.used ?? 0) / 1024 / 1024 / 1024).toFixed(1);
    const sizeGB = ((disk.size ?? 0) / 1024 / 1024 / 1024).toFixed(1);
    let percent = disk.usagePercent;
    if (percent == null && disk.size) percent = (disk.used / disk.size) * 100;
    percent = pct(percent);
    return (
      <Card title={`Disco ${mountLabel}`} variant="outlined">
        <Progress percent={percent} status="active" format={p => `${p}%`} />
        <Text>{usedGB} GB de {sizeGB} GB</Text>
      </Card>
    );
  };

  const renderGpuCard = (g, idx) => {
    const util = pct(g.utilizationGpu);
    const memTotalMB = g.memoryTotalMB ?? null;
    const memUsedMB  = g.memoryUsedMB ?? (memTotalMB != null && g.memoryFreeMB != null ? (memTotalMB - g.memoryFreeMB) : null);
    const memPct = memTotalMB && memUsedMB != null ? pct((memUsedMB / memTotalMB) * 100) : null;
    const memText = memTotalMB ? `${(memUsedMB ?? 0).toFixed(0)} / ${memTotalMB.toFixed(0)} MB` : '—';

    const gpuSeries = history.gpu?.[idx] ?? [];
    const haveSeries = Array.isArray(gpuSeries) && gpuSeries.length >= 2;

    return (
      <Card
        key={`gpu-${idx}`}
        title={<Space wrap><span>GPU #{g.index ?? idx}</span>{g.vendor && <Tag>{g.vendor}</Tag>}{g.model && <Tag>{g.model}</Tag>}</Space>}
        variant="outlined"
      >
        {verGraficos && haveSeries ? (
          (() => {
            const cfg = lineChart(`GPU ${idx} %`, gpuSeries, 'purple');
            return <div style={{ height: 180 }}><Line data={cfg.data} options={cfg.options} /></div>;
          })()
        ) : (
          <Progress percent={util} status="active" format={p => `${p}%`} />
        )}

        <div style={{ marginTop: 8 }}>
          <Text strong>Uso GPU:</Text> <Text>{util}%</Text><br />
          <Text strong>Memoria:</Text>{' '}
          {memPct != null ? (
            <>
              <Progress percent={memPct} size="small" style={{ width: 180, display: 'inline-block', margin: '0 8px' }} />
              <Text>{memText}</Text>
            </>
          ) : <Text>—</Text>}
          <br />
          <Text strong>Temp:</Text> <Text>{g.temperatureGpu != null ? `${g.temperatureGpu} °C` : '—'}</Text>{' '}
          <Text strong style={{ marginLeft: 12 }}>Potencia:</Text> <Text>{g.powerDrawW != null ? `${g.powerDrawW} W` : '—'}</Text>
        </div>
      </Card>
    );
  };

  // ---- Resumen simple de GPU para pestaña General ----
  const renderGpuSummary = (g, idx) => {
    const util = pct(g.utilizationGpu);
    const series = history.gpu?.[idx] ?? [];
    const haveSeries = series.length >= 2;

    return (
      <Card title={`GPU #${g.index ?? idx}`} variant="outlined">
        {verGraficos && haveSeries ? (
          (() => {
            const cfg = lineChart(`GPU ${idx} %`, series, 'purple');
            return <div style={{ height: 160 }}><Line data={cfg.data} options={cfg.options} /></div>;
          })()
        ) : (
          <Progress percent={util} status="active" format={(p) => `${p}%`} />
        )}
        <div style={{ marginTop: 6 }}>
          <Text strong>Uso:</Text> <Text>{util}%</Text>
          <Text strong style={{ marginLeft: 12 }}>Temp:</Text>{' '}
          <Text>{g.temperatureGpu != null ? `${g.temperatureGpu} °C` : '—'}</Text>
        </div>
      </Card>
    );
  };

  const tabsItems = [
    {
      key: '1',
      label: <span style={tabStyle(activeTab, '1')}>General</span>,
      children: (
        <Row gutter={[16, 16]}>
          {/* ⬇️ GPU dentro de la pestaña General (al principio) */}
          {gpu?.controllers?.length
            ? (gpu.controllers).slice(0, 2).map((g, idx) => (
                <Col key={`gpu-summary-${idx}`} xs={24} sm={12} md={8}>
                  {renderGpuSummary(g, idx)}
                </Col>
              ))
            : (
              <Col xs={24}>
                <Empty description="No se detectaron GPUs o métricas no disponibles" />
              </Col>
            )
          }

          {cpu && (
            <Col xs={24} sm={12} md={8}>
              <Card title="Uso de CPU" variant="outlined">
                {verGraficos ? (
                  (() => {
                    const cfg = lineChart('CPU %', history.cpu, 'orange');
                    return <div style={{ height: 180 }}><Line data={cfg.data} options={cfg.options} /></div>;
                  })()
                ) : (
                  <>
                    <Progress percent={pct(cpu.usage)} status="active" format={p => `${p}%`} />
                    <div><Text>{cpu.cores} núcleos</Text></div>
                  </>
                )}
              </Card>
            </Col>
          )}

          {memory && (
            <Col xs={24} sm={12} md={8}>
              <Card title="Memoria RAM" variant="outlined">
                {verGraficos ? (
                  (() => {
                    const cfg = lineChart('RAM %', history.memory, 'green');
                    return <div style={{ height: 180 }}><Line data={cfg.data} options={cfg.options} /></div>;
                  })()
                ) : (
                  <>
                    <Progress percent={pct(memory.usagePercent)} status="active" format={p => `${p}%`} />
                    <Text>{((memory.used ?? 0) / 1024).toFixed(1)} GB de {((memory.total ?? 0) / 1024).toFixed(1)} GB</Text>
                  </>
                )}
              </Card>
            </Col>
          )}

          {network && (
            <Col xs={24} sm={12} md={8}>
              <Card title="Ancho de Banda (Red)" variant="outlined">
                <Text>⬆️ Subida: {((network.tx ?? 0) / 1024 / 1024).toFixed(2)} MB/s</Text><br />
                <Text>⬇️ Bajada: {((network.rx ?? 0) / 1024 / 1024).toFixed(2)} MB/s</Text>
              </Card>
            </Col>
          )}
        </Row>
      ),
    },
    {
      key: '2',
      label: <span style={tabStyle(activeTab, '2')}>Discos</span>,
      children: (
        <>
          <Card style={{ marginBottom: 16 }} title="Resumen de discos">
            <Text strong>Total de discos montados: {totalDiscosMontados}</Text>
          </Card>

          {root && (
            <Row gutter={[16, 16]} justify="center" style={{ marginBottom: 16 }}>
              <Col xs={24} sm={16} md={12} lg={8}>{renderDiskCard(root)}</Col>
            </Row>
          )}

          {totalDiscosMontados === 0 ? (
            <Empty description="No se encontraron montajes /mnt/DiscoX" />
          ) : (
            <Row gutter={[16, 16]}>
              {discos.map((disk, idx) => (
                <Col key={(disk.mount || disk.fs || 'disk') + '-' + idx} xs={24} sm={12} md={8}>
                  {renderDiskCard(disk)}
                </Col>
              ))}
            </Row>
          )}
        </>
      ),
    },
    {
      key: '3',
      label: <span style={tabStyle(activeTab, '3')}>GPU</span>,
      children: (
        !gpu?.controllers?.length ? (
          <Empty description="No se detectaron GPUs o métricas no disponibles" />
        ) : (
          <Row gutter={[16, 16]}>
            {gpu.controllers.map((g, idx) => (
              <Col key={`gpu-col-${idx}`} xs={24} sm={12} md={8}>
                {renderGpuCard(g, idx)}
              </Col>
            ))}
          </Row>
        )
      ),
    },
  ];

  return (
    <>
      <AdminMenu />
      <div style={{ padding: '80px 20px 20px' }}>
        <Title level={2} style={{ color: '#fff' }}>Monitor del Servidor</Title>

        <div style={{ marginBottom: 20 }}>
          <Switch
            checked={verGraficos}
            onChange={setVerGraficos}
            checkedChildren="Vista con gráficos"
            unCheckedChildren="Vista sin gráficos"
          />
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" items={tabsItems} />
      </div>
    </>
  );
}

export default AdminMonitor;
